
import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Combobox } from "@/components/ui/combobox"
import { useUsers } from "@/api/users"
import { useAssignExercisePlan, useExercisePlans } from "@/api/exercise-plans"
import { useGym } from "@/contexts/gym-context"
import { useToast } from "@/hooks/use-toast"

const formSchema = z.object({
  userId: z.string().min(1, { message: "User is required" }),
})

type FormValues = z.infer<typeof formSchema>

interface AssignPlanModalProps {
  planType: "exercise"
  open: boolean
  onOpenChange: (open: boolean) => void
  userId?: string
  onUserAssigned?: () => void
}

export function AssignPlanModal({ planType, open, onOpenChange, userId, onUserAssigned }: AssignPlanModalProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [selectedUser, setSelectedUser] = useState(userId || "")
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const { selectedGym } = useGym()

  // Fetch users filtered by the selected gym
  const { data: users = [] } = useUsers(selectedGym)
  const { data: exercisePlans = [] } = useExercisePlans()
  const plans = exercisePlans
  const [selectedPlan, setSelectedPlan] = useState("")
  const [planSearch, setPlanSearch] = useState("")


  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      userId: "",
    },
  })

  // Fix: always set form value for userId when dialog opens (for validation)
  useEffect(() => {
    if (userId) {
      setSelectedUser(userId)
      form.setValue("userId", userId)
    }
    if (!open) {
      setSelectedPlan("")
      setUserSearch("")
      setPlanSearch("")
    }
  }, [userId, open])

  // Map users to a consistent format
  const formattedUsers =
    users?.map((user) => ({
      id: user.id || user._id || "",
      name: user.name || "",
      surname: user.surname || "",
      email: user.email || "",
      // No avatar property in User, so omit avatar
    })) || []

  // User search state for combobox
  const [userSearch, setUserSearch] = useState("")

  const filteredUsers =
    userSearch.trim() === ""
      ? formattedUsers
      : formattedUsers.filter(
          (u) =>
            u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
            u.surname.toLowerCase().includes(userSearch.toLowerCase()) ||
            u.email.toLowerCase().includes(userSearch.toLowerCase())
        )

  const filteredPlans =
    planSearch.trim() === ""
      ? plans
      : plans?.filter((plan: { name: string }) =>
          plan.name.toLowerCase().includes(planSearch.toLowerCase())
        )

  // Ensure selectedUser is always a string (user id)
  const handleUserSelect = (userId: string) => {
    setSelectedUser(userId)
    form.setValue("userId", userId)
    setIsUserMenuOpen(false)
  }

  // Ensure selectedPlan is always a string (plan id)
  const handlePlanSelect = (planId: string) => {
    setSelectedPlan(planId)
  }

  const assignExercisePlan = useAssignExercisePlan();

  const onSubmit = async (_values: FormValues) => {
    form.setValue("userId", selectedUser)
    try {
      await assignExercisePlan.mutateAsync({
        userId: selectedUser,
        planId: selectedPlan,
      })
      toast({
        title: "Success",
        description: "Exercise plan assigned successfully",
      })
      if (typeof onUserAssigned === "function") onUserAssigned();
      onOpenChange(false)
      form.reset()
      setSelectedUser(userId || "")
      setSelectedPlan("")
    } catch (error) {
      console.error("Error assigning exercise plan:", error)
      toast({
        title: "Error",
        description: "Failed to assign exercise plan",
        variant: "destructive",
      })
    }
  }

  const isLoading = assignExercisePlan.isPending

  const renderUserOption = (option: any) => (
    <div className="flex items-center gap-2">
      {option.avatar ? (
        <img src={option.avatar} alt="avatar" className="w-6 h-6 rounded-full" />
      ) : (
        <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-500 dark:text-gray-300">
          {option.label[0]}
        </div>
      )}
      <div>
        <div className="font-medium text-gray-900 dark:text-gray-100">{option.label}</div>
        {option.description && (
          <div className="text-xs text-gray-500 dark:text-gray-400">{option.description}</div>
        )}
      </div>
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onOpenChange(false)}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] ">
        <DialogHeader>
          <DialogTitle>
            {t("planAssignments.assignExercisePlan")}
          </DialogTitle>
        </DialogHeader>


        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            {/* User Combobox */}
            <Label>{t("planAssignments.selectUser")}</Label>
            <Combobox
              value={selectedUser}
              onValueChange={handleUserSelect}
              options={filteredUsers.map((user: any) => ({
                value: user.id,
                label: user.name + (user.surname ? ` ${user.surname}` : ""),
                description: user.email,
                avatar: user.avatar,
              }))}
              searchValue={userSearch}
              onSearchValueChange={setUserSearch}
              placeholder={t("planAssignments.selectUser")}
              renderOption={renderUserOption}
              disabled={!!userId}
            />
            {/* Plan Combobox */}
            <Label>{t("planAssignments.selectPlan")}</Label>
            <Combobox
              value={selectedPlan}
              onValueChange={handlePlanSelect}
              options={filteredPlans.map((plan: any) => ({
                value: plan._id,
                label: plan.name,
                description: plan.type,
              }))}
              searchValue={planSearch}
              onSearchValueChange={setPlanSearch}
              placeholder={t("planAssignments.selectPlan")}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isLoading || !selectedUser || !selectedPlan}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("planAssignments.assigning")}
                </>
              ) : (
                t("planAssignments.assign")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
